/**
 * Shared product taxonomy for Sales Dialer surfaces.
 *
 * Iter 178.8 (Josh Feb 2026) — single source of truth for the product/
 * solution categories used to tag, filter, and group campaigns across:
 *   • DialerAdminPanels.jsx (admin Campaigns tab — KPIs, chips, groups)
 *   • DialerLeadContext.jsx (agent CampaignGroupedQueue — chips, groups)
 *   • Campaign create / edit forms (tag picker)
 *
 * Why extracted: the same taxonomy was previously duplicated in
 * DialerAdminPanels.jsx and there was no way to add a category (e.g.
 * APCM) once. Now the file is the single seam — append a category
 * here and every surface gets the chip, the color, the group header,
 * and the alias matching for free.
 *
 * Alias matching: campaigns persist their product tags in
 * `filters.product_interest` (array of strings — values come from
 * legacy imports as `mdnet`, `ccm/apcm`, `apcm`, etc.). Each category
 * carries an alias list so non-canonical values still route to the
 * correct group. Order in PRODUCT_CATEGORIES === display order.
 */

export const PRODUCT_CATEGORIES = [
  { code: "CCM",  label: "Chronic Care Mgmt",   color: "#1D9E75", aliases: ["ccm", "chronic care", "chronic care management"] },
  // Iter 178.8 — APCM as a SEPARATE category (Josh Feb 2026). CMS's
  // Advanced Primary Care Management replaces CCM for some contracts;
  // teams need to dial these audiences separately even though the
  // legacy taxonomy collapsed them under "ccm/apcm".
  { code: "APCM", label: "Advanced Primary Care", color: "#14B8A6", aliases: ["apcm", "ccm/apcm", "advanced primary care", "advanced primary care management"] },
  { code: "RPM",  label: "Remote Patient Mon.", color: "#7C3AED", aliases: ["rpm", "remote patient", "remote patient monitoring"] },
  { code: "AWV",  label: "Annual Wellness",     color: "#EA8A1A", aliases: ["awv", "annual wellness", "awv program"] },
  { code: "EHR",  label: "EHR / MDNet",         color: "#2563EB", aliases: ["ehr", "mdnet", "ehr/mdnet"] },
  { code: "RCM",  label: "Revenue Cycle",       color: "#DC2626", aliases: ["rcm", "revenue cycle", "revenue cycle management"] },
];

export const PRODUCT_OTHER = {
  code: "OTHER",
  label: "Other / Uncategorized",
  color: "#94A3B8",
  aliases: [],
};

/**
 * Map an arbitrary product_interest string to a canonical category
 * code. Returns null if nothing matches (caller decides whether to
 * fall through to OTHER).
 */
export function normalizeProductCode(raw) {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase();
  for (const cat of PRODUCT_CATEGORIES) {
    if (cat.aliases.includes(s)) return cat.code;
  }
  return null;
}

/**
 * Return the FIRST canonical category a campaign belongs to (for
 * chip-filter scoping and KPI tagging). Falls through to OTHER.
 */
export function categorizeCampaign(campaign) {
  const arr = Array.isArray(campaign?.filters?.product_interest)
    ? campaign.filters.product_interest
    : [];
  for (const raw of arr) {
    const code = normalizeProductCode(raw);
    if (code) return code;
  }
  return PRODUCT_OTHER.code;
}

/**
 * Return ALL category codes a campaign belongs to (deduped, ordered).
 * Iter 178.8 — campaigns can be tagged with MULTIPLE products and
 * should appear under each product group on both admin and agent
 * surfaces. Falls through to [OTHER] if no recognized tags.
 */
export function categoryCodesForCampaign(campaign) {
  const arr = Array.isArray(campaign?.filters?.product_interest)
    ? campaign.filters.product_interest
    : [];
  const seen = [];
  for (const raw of arr) {
    const code = normalizeProductCode(raw);
    if (code && !seen.includes(code)) seen.push(code);
  }
  return seen.length ? seen : [PRODUCT_OTHER.code];
}

/** Look up a category by code; returns PRODUCT_OTHER if unknown. */
export function getCategory(code) {
  return PRODUCT_CATEGORIES.find((c) => c.code === code) || PRODUCT_OTHER;
}

/**
 * Given a category code, return the CANONICAL alias to persist in
 * `filters.product_interest`. We always write the lowercase code so
 * the round-trip is stable and human-readable.
 */
export function canonicalAliasForCode(code) {
  const cat = PRODUCT_CATEGORIES.find((c) => c.code === code);
  if (!cat) return null;
  return cat.aliases[0] || code.toLowerCase();
}
